// @flow
import React from "react";

import classnames from "classnames";
import PropTypes from "prop-types";
import { FieldRenderProps } from "react-final-form";

import { Div } from "Common/HTML";
import Icon from "Common/Icon";

import { Input } from "./Input";

import makeBem from "Utils/makeBem";

const bem = makeBem("Rating");

export interface RatingProps extends Partial<FieldRenderProps<number, HTMLElement>> {
  /* Max amount of stars to render */
  max?: number;
  /* Amount of filled-in stars to display */
  value?: number;
  /* Spacer size used between stars */
  gap?: number;
  /* Additional classnames to apply */
  className?: string;
  size?: string;
  onChange?: (_value: number) => void;
}

const defaultProps = {
  value: 0,
};

/**
 * Rating - given a value and max, renders a row of stars
 * @see https://www.figma.com/file/dzxBDpkpa0EkwgZ2unzd4R/Design-System-Draft?node-id=689%3A7786&mode=dev
 */

export const Rating: React.FC<RatingProps> = ({ input, meta, max = 5, size, value: controlledValue, gap = 1, step = 1, className, onChange, disabled }) => {
  const [hoverIndex, setHoverIndex] = React.useState<number | null>(null);
  const [hoverFraction, setHoverFraction] = React.useState<boolean>(false);
  const value = input?.value ?? controlledValue ?? 0;

  const handleMouseEnter = (index: number, fraction: boolean) => {
    setHoverIndex(index);
    setHoverFraction(fraction);
  };

  const handleMouseLeave = () => {
    setHoverIndex(null);
    setHoverFraction(false);
  };

  const handleClick = (index: number, isHalf: boolean) => {
    const newValue = isHalf ? index + 0.5 : index + 1;
    if (input) {
      input.onChange(newValue);
    }
    if (onChange) {
      onChange(newValue);
    }
  };

  const renderStar = (i: number) => {
    const isSelected = i < Math.floor(value);
    const isHalfSelected = i === Math.floor(value) && value % 1 >= 0.5;
    const fullHover = hoverIndex != null && i < hoverIndex;
    const halfHover = hoverIndex != null && i === hoverIndex && hoverFraction && step < 1;
    const exactHover = hoverIndex != null && i === hoverIndex && !hoverFraction;

    let iconType = "star";
    if (isHalfSelected) {
      iconType = "star-half-alt";
    }
    if (fullHover || exactHover) {
      iconType = "star";
    }
    if (halfHover && i >= value) {
      iconType = "star-half-alt";
    }

    return (
      <Icon
        key={i}
        // pr={i < max - 1 ? gap : 0}
        pr={gap}
        icon={iconType}
        flavor={isSelected || fullHover || halfHover || exactHover || isHalfSelected ? "solid" : "regular"}
        onMouseEnter={() => !disabled && handleMouseEnter(i, false)}
        onMouseMove={(e) => {
          if (!disabled) {
            const rect = e.currentTarget.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const isHalf = mouseX < rect.width / 2 && step < 1;
            handleMouseEnter(i, isHalf);
          }
        }}
        onMouseLeave={handleMouseLeave}
        onClick={() => handleClick(i, hoverFraction)}
        style={!disabled ? { cursor: "pointer" } : undefined}
        size={size}
        data-testid={isSelected || fullHover || halfHover || exactHover || isHalfSelected ? "star-filled" : "star-unfilled"}
      />
    );
  };

  return (
    <Div className={classnames("Rating", className)} aria-label={`Rated ${value} out of ${max} stars`}>
      {Array.from({ length: max }, (_, i) => renderStar(i))}
      {input && <input type="hidden" {...input} />}
      {meta?.touched && meta?.error && <span className="error">{meta.error}</span>}
    </Div>
  );
};

Rating.propTypes = {
  max: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
  gap: PropTypes.number,
};

Rating.defaultProps = defaultProps;

export default Rating;
